import React, { useState, useEffect } from 'react';
import { Save, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import Notification from './Notification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [formData, setFormData] = useState({
    business_name: '',
    business_phone: '',
    business_email: '',
    quote_prefix: 'Q-',
    quote_start_number: 1000,
    quote_increment: 2,
    current_quote_number: 1000,
    logo_url: ''
  });

  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification('error', 'Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        logo_url: response.data.logo_url
      }));
      
      showNotification('success', 'Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      showNotification('error', 'Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/settings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  return (
    <div className="form-container">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      <div className="form-header">
        <h2>Business Settings</h2>
        <p>Update your business information and quote preferences</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Logo Upload Section */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#333' }}>Business Logo</h3>
          
          {formData.logo_url && (
            <div style={{ marginBottom: '1rem' }}>
              <img 
                src={`${BACKEND_URL}${formData.logo_url}`} 
                alt="Business Logo" 
                style={{
                  maxWidth: '200px',
                  maxHeight: '100px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  backgroundColor: '#fff'
                }}
              />
            </div>
          )}
          
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploading}
              style={{ display: 'none' }}
              id="logo-upload"
            />
            <label
              htmlFor="logo-upload"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: uploading ? '#94b4d6' : '#4A90E2',
                color: '#fff',
                borderRadius: '8px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              {uploading ? (
                <>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  {formData.logo_url ? 'Change Logo' : 'Upload Logo'}
                </>
              )}
            </label>
          </div>
        </div>

        {/* Business Information */}
        <div className="form-group">
          <label className="form-label">Business Name *</label>
          <input
            type="text"
            name="business_name"
            className="form-input"
            value={formData.business_name}
            onChange={handleChange}
            placeholder="Your Business Name"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Business Phone *</label>
          <input
            type="tel"
            name="business_phone"
            className="form-input"
            value={formData.business_phone}
            onChange={handleChange}
            placeholder="(403) 555-1234"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Business Email *</label>
          <input
            type="email"
            name="business_email"
            className="form-input"
            value={formData.business_email}
            onChange={handleChange}
            placeholder="info@yourbusiness.com"
            required
          />
        </div>

        {/* Quote Number Configuration */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#333' }}>Quote Number Settings</h3>
          <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: '1rem' }}>
            Customize how your quote numbers are generated. Default is Q-1000 with increment of 2.
          </p>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Prefix (e.g., Q-, INV-)</label>
              <input
                type="text"
                name="quote_prefix"
                className="form-input"
                value={formData.quote_prefix}
                onChange={handleChange}
                placeholder="Q-"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Increment By</label>
              <input
                type="number"
                name="quote_increment"
                className="form-input"
                value={formData.quote_increment}
                onChange={handleNumberChange}
                min="1"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Current Quote Number</label>
            <input
              type="number"
              name="current_quote_number"
              className="form-input"
              value={formData.current_quote_number}
              onChange={handleNumberChange}
              min="0"
            />
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              Next quote will be: {formData.quote_prefix}{formData.current_quote_number + formData.quote_increment}
            </p>
          </div>
        </div>

        {saved && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '1.1rem'
          }}>
            Settings saved successfully!
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-large">
          <Save size={24} />
          Save Settings
        </button>
      </form>
    </div>
  );
};

export default SettingsPage;